import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { useBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  UserQuery_node_TEDSYSUser,
  UserQuery_node_TEDSYSUser__Associations_Franvaro_All,
} from '../../Queries/_QueryTypes/UserQuery';
import { FranvaroContext } from '../../Context/FranvaroContext/FranvaroContext';
import { mergeBusinessDataFormValues } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { AUTOMATICALLY_ADDED_VALUE_PREFIX } from '../../../../../Leverantor/ObjektVyer/FakturaDel/Components/FakturaDelContextManager/FakturaDelContextManager';
import shortid from 'shortid';
import { ModelContext } from '../../../../../../../Model/ModelContext/ModelContext';
import { getAttribute } from '../../../../../../../Model/avenyModelUtils';
import EditableAttribute from '../../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import { EDInputGroup } from '../../../../../../../BaseComponents/InputGroup/EDInputGroup';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDChip from '../../../../../../../BaseComponents/Chip/EDChip';
// eslint-disable-next-line aveny/mui-import-rule
import { AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete';
import { OnChange } from '../../../../../../../Components/Attributes/EditableAttribute/EditComponentTypes';
import { makeStyles } from 'tss-react/mui';
import { useQuery } from '@apollo/client';
import {
  AllUsersQuery,
  AllUsersQueryVariables,
  AllUsersQuery_db_SYSUser_All,
} from '../../Queries/_QueryTypes/AllUsersQuery';
import allUsersQuery from '../../Queries/AllUsersQuery.gql';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { useIsBreakpointOrSmaller } from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import { mergeBusinessDataMutations } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/MergeBusinessDataMutations';
import { SYSFranvaroSysoptionQuery_db_SYSOptions_All } from '../../Context/FranvaroContext/Queries/_QueryTypes/SYSFranvaroSysoptionQuery';
import { EK_BOKFORDER_UTANORDNING_ACTIVE } from '../../../../../Ekonomi/Rutiner/Bokforder/Const/BokforderSysoptionConsts';

interface IProps {
  id: string;
}

type BFOOptionType = {
  label: 'Bokföringsorder';
  value: 'FOR_BOKFORDER';
};
type UAOptionType = {
  label: 'Utanordning';
  value: 'FOR_UTANORDNING';
};

type IsForOptionsType = BFOOptionType | UAOptionType;

const useStyles = makeStyles()(theme => ({
  tag: {
    backgroundColor: theme.palette.grey[300],
    height: 'auto',
    margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)}`,
    minHeight: theme.spacing(3),
  },
}));

const FranvaroAddDialog: React.FC<IProps> = props => {
  const { classes } = useStyles();
  const { activeDatabase, userID } = useContext(ActiveDatabaseContext);
  const formContext = useBusinessDataFormContext<UserQuery_node_TEDSYSUser>();
  const franvaroContext = useContext(FranvaroContext);
  const modelContext = useContext(ModelContext);
  const isSmallScreen = useIsBreakpointOrSmaller('sm');
  const reservFieldRef = useRef<HTMLInputElement>(undefined);

  const [pickedIsForOptions, setPickedIsForOptions] =
    useState<IsForOptionsType[]>();
  const [franvaro, setFranvaro] =
    useState<UserQuery_node_TEDSYSUser__Associations_Franvaro_All>(
      {} as UserQuery_node_TEDSYSUser__Associations_Franvaro_All
    );

  const utanordningActive = useMemo(() => {
    return (
      franvaroContext.sysoptions?.find(
        (sysoption: SYSFranvaroSysoptionQuery_db_SYSOptions_All) =>
          sysoption?.OptionKey === EK_BOKFORDER_UTANORDNING_ACTIVE
      )?.OptionData === 'Y'
    );
  }, [franvaroContext.sysoptions]);

  const franvaroData = useMemo(() => {
    const data =
      formContext?.formState.currentValues?._Associations?.Franvaro_All?.find(
        franvaro => franvaro.id === props.id
      );

    if (data) {
      setFranvaro(
        data ?? ({} as UserQuery_node_TEDSYSUser__Associations_Franvaro_All)
      );
    }
    return data;
  }, [
    formContext?.formState.currentValues?._Associations?.Franvaro_All,
    props.id,
  ]);

  if (
    franvaroContext.registerFranvaroDialogOpen &&
    franvaroData &&
    pickedIsForOptions == undefined
  ) {
    const alreadyPickedOptions: IsForOptionsType[] = franvaroData
      ? Object.entries(franvaroData).reduce((prevValue, currValue) => {
          const key = currValue[0];
          const value = currValue[1];

          if (key === 'FOR_BOKFORDER' && value) {
            return [
              ...prevValue,
              { label: 'Bokföringsorder', value: 'FOR_BOKFORDER' },
            ];
          }
          if (key === 'FOR_UTANORDNING' && value) {
            return [
              ...prevValue,
              { label: 'Utanordning', value: 'FOR_UTANORDNING' },
            ];
          }
          return prevValue;
        }, [] as IsForOptionsType[])
      : [];

    setPickedIsForOptions(alreadyPickedOptions);
  }

  const { data, loading } = useQuery<AllUsersQuery, AllUsersQueryVariables>(
    allUsersQuery,
    {
      variables: { dbName: activeDatabase ?? '' },
      skip: !activeDatabase,
    }
  );

  const franvaroModel = modelContext.avenyCollectionMap.get('sysfranvaro');

  const dateFromAttribute = getAttribute(
    'SYSFranvaro',
    '',
    'DATUM_FROM',
    modelContext.avenyCollectionMap
  );

  const dateToAttribute = getAttribute(
    'SYSFranvaro',
    '',
    'DATUM_TOM',
    modelContext.avenyCollectionMap
  );

  const noteAttribute = getAttribute(
    'SYSFranvaro',
    '',
    'NOTERING',
    modelContext.avenyCollectionMap
  );

  if (
    !dateFromAttribute ||
    !dateToAttribute ||
    !noteAttribute ||
    !franvaroModel
  ) {
    throw new Error('Problem att ladda in Attribute');
  }

  const getIsForOptionSelected = useCallback(
    (option: IsForOptionsType, value: IsForOptionsType) =>
      option?.value === value?.value,
    []
  );

  const getChip =
    (getTagProps: AutocompleteRenderGetTagProps) =>
    (option: IsForOptionsType, index: number) => {
      const label = option.label;
      const chipDisabled = false;

      return (
        <>
          {chipDisabled ? (
            <EDTooltip
              key={index}
              title={'Avstängd'}
              enterDelay={500}
              enterTouchDelay={500}
              enableClickOnTouch
            >
              <span>
                <EDChip
                  label={label}
                  {...getTagProps({ index })}
                  deleteIcon={chipDisabled ? <> </> : undefined}
                  disabled={chipDisabled}
                  variant="outlined"
                />
              </span>
            </EDTooltip>
          ) : (
            <EDChip
              label={label}
              {...getTagProps({ index })}
              variant="outlined"
            />
          )}
        </>
      );
    };

  const isForOnChange: OnChange<IsForOptionsType, true> = useCallback(
    (newValue, event) => {
      if (newValue != null) {
        if (Array.isArray(newValue)) {
          setPickedIsForOptions([...newValue]);
        }
      }
      if (event?.type === 'blur') {
        setTimeout(() => reservFieldRef?.current?.focus?.(), 10);
      }
    },
    []
  );

  const isForOptions: IsForOptionsType[] = useMemo(() => {
    const options: IsForOptionsType[] = [];

    if (formContext?.data?.harKodbehorighetBokforder) {
      options.push({ label: 'Bokföringsorder', value: 'FOR_BOKFORDER' });
    }

    if (formContext?.data?.harKodbehorighetUtanordning && utanordningActive) {
      options.push({ label: 'Utanordning', value: 'FOR_UTANORDNING' });
    }

    return options;
  }, [
    formContext?.data?.harKodbehorighetBokforder,
    formContext?.data?.harKodbehorighetUtanordning,
    utanordningActive,
  ]);

  const pickedUser = useMemo(
    () =>
      (data?.db?.SYSUser_All ?? []).find(
        user => user?.id === franvaroContext.franvaroReservID
      ) ?? null,
    [data?.db?.SYSUser_All, franvaroContext.franvaroReservID]
  );

  const userOptions = useMemo(
    () =>
      data?.db?.SYSUser_All?.filter(user => {
        if (
          pickedIsForOptions == undefined ||
          pickedIsForOptions.length === 0 ||
          userID === user.id
        ) {
          return false;
        }

        if (
          pickedIsForOptions.some(
            option =>
              option.value === 'FOR_BOKFORDER' ||
              option.value === 'FOR_UTANORDNING'
          )
        ) {
          const hasEveryOption = pickedIsForOptions.every(option => {
            return user._Associations?.Kontobegransningar_All?.some(kodbeh => {
              const isForOption = kodbeh?.[option.value];

              return isForOption;
            });
          });

          return hasEveryOption;
        }

        return false;
      }) ?? [],
    [data?.db?.SYSUser_All, pickedIsForOptions, userID]
  );

  const isSaveButtonDisabled: () => React.ReactNode[] = useCallback(() => {
    const disabledReasons = [];

    if (franvaro?.DATUM_TOM) {
      if (
        new Date(franvaro?.DATUM_TOM) < new Date(franvaro?.DATUM_FROM) &&
        franvaro?.DATUM_FROM
      ) {
        disabledReasons.push(
          <>
            Till datum måste vara större än från datum <br />
          </>
        );
      }
    }

    if (!franvaro?.DATUM_FROM) {
      disabledReasons.push(
        <>
          Du måste ange ett från datum <br />
        </>
      );
    }

    if (!franvaro?.NOTERING) {
      disabledReasons.push(
        <>
          Du måste ange en notering <br />
        </>
      );
    }

    if (pickedIsForOptions == undefined || pickedIsForOptions?.length === 0) {
      disabledReasons.push(
        <>
          Du måste ange för vilka funktioner frånvaron gäller för <br />
        </>
      );
    }

    if (!franvaroContext.franvaroReservID) {
      disabledReasons.push(
        <>
          Du måste ange en reserv <br />
        </>
      );
    }

    const pickedUserIsInOptions = userOptions.some(
      userOption => userOption.id === franvaroContext.franvaroReservID
    );

    if (franvaroContext.franvaroReservID && !pickedUserIsInOptions) {
      disabledReasons.push(
        <>
          Vald användare är inte en giltig reserv <br />
        </>
      );
    }

    if (
      !franvaro?.DATUM_FROM &&
      !franvaro?.NOTERING &&
      !franvaroContext.franvaroReservID &&
      (pickedIsForOptions == undefined || pickedIsForOptions?.length === 0)
    ) {
      disabledReasons.splice(0, disabledReasons.length);
      disabledReasons.push('Du måste fylla i alla obligatoriska fält');
    }

    return disabledReasons;
  }, [
    franvaro?.DATUM_FROM,
    franvaro?.DATUM_TOM,
    franvaro?.NOTERING,
    franvaroContext.franvaroReservID,
    pickedIsForOptions,
    userOptions,
  ]);

  return (
    <EDDialog
      open={franvaroContext.registerFranvaroDialogOpen}
      onExited={() => {
        setPickedIsForOptions(undefined);
        franvaroContext.setFranvaroReservID(null);
        franvaroContext.setEditFranvaro(undefined);
        setFranvaro({} as UserQuery_node_TEDSYSUser__Associations_Franvaro_All);
      }}
      onClose={() => {
        formContext?.formStateDispatch({
          type: 'CANCEL',
        });
        franvaroContext.setRegisterFranvaroDialogOpen(false);
      }}
      title={
        franvaroContext.editFranvaro
          ? 'Redigera frånvaro'
          : 'Registrera frånvaro'
      }
      footerActions={[
        {
          label: 'Avbryt',
          onClick: () => {
            formContext?.formStateDispatch({
              type: 'CANCEL',
            });
            franvaroContext.setRegisterFranvaroDialogOpen(false);
          },
        },
        {
          label: 'Spara',
          onClick: () => {
            if (franvaroContext.editFranvaro != null) {
              let values: Record<string, boolean> = {};

              isForOptions.forEach(option => {
                const optionValue = franvaroData?.[option.value];
                const pickedValue = pickedIsForOptions?.find(
                  pickedOption => pickedOption.value === option.value
                );

                if (optionValue != undefined && !pickedValue) {
                  values[option.value] = false;
                } else if (optionValue === undefined && pickedValue) {
                  values[option.value] = true;
                } else if (optionValue != undefined && pickedValue) {
                  values[option.value] = true;
                }
              });

              const updateObject: FormUpdateAssociatonType = {
                operation: 'Update_Values',
                associationName: 'Franvaro',
                id: franvaroData?.id ?? '',
                values: values,
              };

              const newValues: FormUpdateAssociatonType[] = [updateObject];

              const mergedValues = mergeBusinessDataMutations(
                formContext?.formState.values ?? {},
                getFormUpdateValue(newValues, formContext?.data ?? {})
              );

              formContext?.saveForm(mergedValues);
            } else {
              const id =
                AUTOMATICALLY_ADDED_VALUE_PREFIX +
                'reserv' +
                shortid.generate();

              let values: Record<string, string | boolean> = {
                DATUM_FROM: franvaro?.DATUM_FROM ?? '',
                DATUM_TOM: franvaro?.DATUM_TOM ?? '',
                NOTERING: franvaro?.NOTERING ?? '',
              };

              pickedIsForOptions?.forEach(option => {
                values[option.value] = true;
              });

              const updateObject: FormUpdateAssociatonType = {
                operation: 'Add_Values',
                associationName: 'Franvaro',
                id: id,
                values: values,
              };

              const newValues: FormUpdateAssociatonType[][] = [
                [
                  updateObject,
                  {
                    associationName: 'Anvandare',
                    operation: 'Ref',
                    id: formContext?.formState.currentValues?.id,
                  },
                ],
                [
                  updateObject,
                  {
                    associationName: 'Reserv',
                    operation: 'Ref',
                    id: franvaroContext.franvaroReservID ?? '',
                  },
                ],
              ];

              formContext?.saveForm(
                mergeBusinessDataFormValues(
                  getFormUpdateValue(
                    newValues,
                    formContext.formState.currentValues ?? {}
                  ),
                  {}
                )
              );
            }

            franvaroContext.setRegisterFranvaroDialogOpen(false);
          },
          buttonProps: {
            disabled:
              isSaveButtonDisabled().length !== 0 ||
              formContext?.formState.loading ||
              formContext?.mutationLoading ||
              loading,
            disabledReason: isSaveButtonDisabled(),
          },
        },
      ]}
    >
      {loading ? (
        <EDAvenyProgress />
      ) : (
        <EDInputGroup>
          {franvaroContext.editFranvaro != null && franvaro ? (
            <>
              <EditableAttribute
                attribute={dateFromAttribute}
                collection={franvaroModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  formContext?.update([
                    {
                      operation: 'Update_Values',
                      associationName: 'Franvaro',
                      id: props.id,
                      values: {
                        DATUM_FROM: newValue,
                      },
                    },
                  ]);
                }}
                required
                size={isSmallScreen ? 'large' : 'medium'}
                value={franvaroData?.DATUM_FROM ?? ''}
                paddingX={1}
              />

              <EditableAttribute
                attribute={dateToAttribute}
                collection={franvaroModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  formContext?.update([
                    {
                      operation: 'Update_Values',
                      associationName: 'Franvaro',
                      id: props.id,
                      values: {
                        DATUM_TOM: newValue,
                      },
                    },
                  ]);
                }}
                size={isSmallScreen ? 'large' : 'medium'}
                value={franvaroData?.DATUM_TOM ?? ''}
                paddingX={1}
              />

              <EditableAttribute
                attribute={noteAttribute}
                collection={franvaroModel}
                disabled={false}
                editMode={true}
                multiline
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  let value = newValue;
                  if (typeof value === 'string') {
                    value = value.trim();
                  }

                  formContext?.update([
                    {
                      operation: 'Update_Values',
                      associationName: 'Franvaro',
                      id: props.id,
                      values: {
                        NOTERING: value,
                      },
                    },
                  ]);
                }}
                required
                size={!isSmallScreen ? 'large' : undefined}
                value={franvaroData?.NOTERING ?? ''}
                paddingX={1}
              />

              <EDAutoCompleteEdit<IsForOptionsType, true>
                classes={{
                  tag: classes.tag,
                }}
                disabled={!franvaro}
                fullWidth
                getOptionLabel={option => option.label}
                getOptionSelected={getIsForOptionSelected}
                hideClear
                label="Gäller för"
                multiple
                onChange={isForOnChange}
                options={isForOptions}
                paddingX={1}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map(getChip(getTagProps))
                }
                required
                value={pickedIsForOptions}
                testId={'franvaro-Is-For'}
              />

              <EDAutoCompleteEdit<AllUsersQuery_db_SYSUser_All>
                fullWidth
                paddingX={1}
                getOptionLabel={option =>
                  option.Fullname ?? 'Kunde inte hitta namn på Användaren'
                }
                getOptionSelected={option => {
                  return option.id === franvaro?._Associations?.Reserv?.id;
                }}
                label="Reserv"
                onChange={option => {
                  const franvaroObject: FormUpdateAssociatonType = {
                    operation: 'Update_Values',
                    associationName: 'Franvaro',
                    id: franvaroData?.id ?? '',
                  };
                  if (option) {
                    if (!Array.isArray(option)) {
                      franvaroContext.setFranvaroReservID(option.id);
                      formContext?.update([
                        franvaroObject,
                        {
                          associationName: 'Reserv',
                          operation: 'Ref',
                          id: option.id,
                        },
                      ]);
                    }
                  } else {
                    franvaroContext.setFranvaroReservID(null);
                    formContext?.update([
                      franvaroObject,
                      {
                        associationName: 'Reserv',
                        operation: 'Ref',
                        id: null,
                      },
                    ]);
                  }
                }}
                options={userOptions}
                required
                value={
                  (data?.db?.SYSUser_All ?? []).find(
                    user => user?.id === franvaroContext.franvaroReservID
                  ) ?? null
                }
                testId="reserv"
              />
            </>
          ) : (
            <>
              <EditableAttribute
                attribute={dateFromAttribute}
                collection={franvaroModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  setFranvaro({
                    ...franvaro,
                    DATUM_FROM: newValue,
                  });
                }}
                required
                size={isSmallScreen ? 'large' : 'medium'}
                value={franvaro?.DATUM_FROM}
                paddingX={1}
              />

              <EditableAttribute
                attribute={dateToAttribute}
                collection={franvaroModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  setFranvaro({ ...franvaro, DATUM_TOM: newValue });
                }}
                size={isSmallScreen ? 'large' : 'medium'}
                value={franvaro?.DATUM_TOM}
                paddingX={1}
              />

              <EditableAttribute
                attribute={noteAttribute}
                collection={franvaroModel}
                disabled={false}
                editMode={true}
                multiline
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  setFranvaro({ ...franvaro, NOTERING: newValue });
                }}
                required
                size={!isSmallScreen ? 'large' : undefined}
                value={franvaro?.NOTERING}
                paddingX={1}
              />

              <EDAutoCompleteEdit<IsForOptionsType, true>
                classes={{
                  tag: classes.tag,
                }}
                disabled={!franvaro}
                fullWidth
                getOptionLabel={option => option.label}
                getOptionSelected={getIsForOptionSelected}
                hideClear
                label="Gäller för"
                multiple
                onChange={isForOnChange}
                options={isForOptions}
                paddingX={1}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map(getChip(getTagProps))
                }
                required={
                  pickedIsForOptions == undefined ||
                  pickedIsForOptions.length === 0
                }
                value={pickedIsForOptions}
                testId={'franvaro-Is-For'}
              />

              <EDAutoCompleteEdit<AllUsersQuery_db_SYSUser_All>
                fullWidth
                paddingX={1}
                disabled={
                  (pickedIsForOptions == undefined ||
                    pickedIsForOptions.length === 0) &&
                  franvaroContext.franvaroReservID == null
                }
                getOptionLabel={option =>
                  option.Fullname ?? 'Kunde inte hitta namn på Användaren'
                }
                getOptionSelected={option => {
                  return option.id === franvaroContext.franvaroReservID;
                }}
                label="Reserv"
                onChange={option => {
                  if (option) {
                    if (!Array.isArray(option)) {
                      franvaroContext.setFranvaroReservID(option.id);
                    }
                  } else {
                    franvaroContext.setFranvaroReservID(null);
                  }
                }}
                options={userOptions}
                required
                inputRef={reservFieldRef}
                value={pickedUser}
                testId="reserv"
              />
            </>
          )}
        </EDInputGroup>
      )}
    </EDDialog>
  );
};

export default FranvaroAddDialog;
